/**
 * EscalationManagementPage — `/customer-service/escalations`.
 *
 * Lists all cases with status=ESCALATED across the operator's scope.
 * Wired to GET /v1/service-desk/cases?status=ESCALATED on service-service-desk.
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Clock } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { listServiceCases } from '../../lib/api/service-desk';
import { PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

export function EscalationManagementPage(): JSX.Element {
  const escalatedQuery = useQuery({
    queryKey: ['cs', 'cases', 'escalations'],
    queryFn: () => listServiceCases({ status: 'ESCALATED', pageSize: 50 }),
    retry: false,
    staleTime: 30_000,
  });

  const cases = escalatedQuery.data?.items ?? [];
  const totalEscalated = escalatedQuery.data?.total ?? 0;

  const tier1 = cases.filter((c) => c.escalationTier === 1).length;
  const tier2 = cases.filter((c) => c.escalationTier === 2).length;
  const tier3plus = cases.filter((c) => c.escalationTier >= 3).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Escalation Management"
        description="Escalated case queue — cases awaiting senior CSR attention."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total escalated"
          value={escalatedQuery.isPending ? '…' : String(totalEscalated)}
          hint="Active escalated cases"
          icon={ArrowUp}
          tone="warning"
        />
        <KPICard
          title="Tier 1"
          value={escalatedQuery.isPending ? '…' : String(tier1)}
          hint="First escalation"
          tone="warning"
        />
        <KPICard
          title="Tier 2"
          value={escalatedQuery.isPending ? '…' : String(tier2)}
          hint="Second escalation"
          tone="warning"
        />
        <KPICard
          title="Tier 3+"
          value={escalatedQuery.isPending ? '…' : String(tier3plus)}
          hint="Critical escalation"
          icon={Clock}
          tone="warning"
        />
      </div>

      {escalatedQuery.isPending ? (
        <p className="text-sm text-muted">Loading escalated cases…</p>
      ) : escalatedQuery.isError ? (
        <div
          className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
          data-testid="escalations-error"
        >
          Failed to load escalations: {(escalatedQuery.error as Error).message}
        </div>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="escalations-table">
            <thead className="bg-amber-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Case #</th>
                <th className="text-left px-4 py-2">Title</th>
                <th className="text-left px-4 py-2">Tier</th>
                <th className="text-left px-4 py-2">Priority</th>
                <th className="text-left px-4 py-2">SLA Target</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {cases.map((c) => (
                <tr
                  key={c.id}
                  className="hover:bg-amber-50"
                  data-testid={`escalated-row-${c.id}`}
                >
                  <td className="px-4 py-2 font-mono text-xs text-gray-900">{c.caseNumber}</td>
                  <td className="px-4 py-2 text-xs max-w-48 truncate">{c.title}</td>
                  <td className="px-4 py-2">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                      <ArrowUp className="w-3 h-3" /> L{c.escalationTier}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={c.priority} />
                  </td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {c.slaTarget ? new Date(c.slaTarget).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Link
                      to={`/customer-service/complaints/${encodeURIComponent(c.id)}`}
                      className="text-xs font-semibold text-nipost-green hover:underline"
                    >
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {cases.length === 0 ? (
            <p
              className="p-8 text-center text-sm text-muted"
              data-testid="escalations-empty"
            >
              No escalated cases.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
